import { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { format, subMonths } from 'date-fns'
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import LoadingSpinner from '../UI/LoadingSpinner'
import { 
  FileText, 
  Download, 
  Eye, 
  DollarSign, 
  TrendingUp, 
  Calendar,
  CreditCard,
  Wallet,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import Button from '../UI/Button'
import api from '../../services/api'
import { paymentsAPI } from '../../services/api'
import { organizationsAPI } from '../../services/organizationsAPI'
import InvoiceDetailModal from '../Modals/InvoiceDetailModal'
import { jsPDF } from 'jspdf'

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

export default function InvoiceTab({ organizationId }) {
  const [selectedPeriod, setSelectedPeriod] = useState('6months')
  const [expandedSection, setExpandedSection] = useState('overview')
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  
  // Fetch payments for the organization (these are the invoices) - optimized caching
  const { data: paymentsData, isLoading: paymentsLoading } = useQuery(
    ['payments', organizationId],
    () => paymentsAPI.getByOrganization(organizationId).then(r => r.data),
    { 
      enabled: !!organizationId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        console.error('Error fetching payments:', error)
      }
    }
  )
  
  // Fetch subscriptions for cost calculation - optimized caching
  const { data: subscriptionsData, isLoading: subscriptionsLoading } = useQuery(
    ['subscriptions', organizationId],
    () => api.get(`/subscriptions/organization/${organizationId}`).then(r => r.data),
    { 
      enabled: !!organizationId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
    }
  )

  // Fetch organization details for invoice PDF - optimized caching
  const { data: orgData, isLoading: orgLoading } = useQuery(
    ['organization', organizationId],
    () => organizationsAPI.getById(organizationId),
    { 
      enabled: !!organizationId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        console.error('Error fetching organization:', error)
      }
    }
  )
  // Match the pattern used in OrganizationDetail.jsx
  const organization = orgData?.data?.data?.organization || null
  
  // Transform payments to invoice format for compatibility with existing code
  const rawPayments = paymentsData?.data?.payments || []
  const subscriptions = subscriptionsData?.data?.subscriptions || []
  
  // Calculate due date based on billing cycle (same logic as subscription tab)
  const calculateDueDate = (subscriptionCreatedAt, billingCycle) => {
    if (!billingCycle || !subscriptionCreatedAt) {
      return null
    }

    try {
      const startDate = new Date(subscriptionCreatedAt)
      const dueDate = new Date(startDate)

      if (billingCycle === 'monthly') {
        // Add 1 month to the start date
        dueDate.setMonth(dueDate.getMonth() + 1)
      } else if (billingCycle === 'yearly') {
        // Add 1 year to the start date
        dueDate.setFullYear(dueDate.getFullYear() + 1)
      } else {
        return null
      }

      return dueDate
    } catch (error) {
      console.error('Error calculating due date:', error)
      return null
    }
  }

  // Create a map of subscription_id -> subscription for quick lookup
  const subscriptionsMap = useMemo(() => {
    const map = {}
    subscriptions.forEach(sub => {
      map[sub.id] = sub
    })
    return map
  }, [subscriptions])

  // Transform payment data to invoice-like format
  const invoices = rawPayments.map(payment => {
    const paymentDate = payment.payment_date || payment.created_at
    let billingPeriod = 'N/A'
    
    if (paymentDate) {
      try {
        billingPeriod = format(new Date(paymentDate), 'MMM yyyy')
      } catch (e) {
        console.warn('Invalid date for payment:', payment.id, paymentDate)
      }
    }

    // Get subscription to calculate due date
    const subscription = payment.subscription_id ? subscriptionsMap[payment.subscription_id] : null
    const subscriptionCreatedAt = subscription?.created_at || null
    const billingCycle = payment.billing_cycle || subscription?.billing_cycle || null
    const dueDate = calculateDueDate(subscriptionCreatedAt, billingCycle)
    
    return {
      id: payment.id,
      invoice_number: payment.transaction_id || `PAY-${payment.id.substring(0, 8).toUpperCase()}`,
      created_at: paymentDate,
      date: paymentDate,
      amount: parseFloat(payment.amount || 0),
      total: parseFloat(payment.amount || 0),
      status: payment.payment_status === 'completed' ? 'paid' : 
              payment.payment_status === 'failed' ? 'failed' : 'pending',
      billing_period: billingPeriod,
      product_id: payment.product_id,
      product_name: payment.product_name || 'Unknown Product',
      subscription_id: payment.subscription_id,
      plan_name: payment.plan_name,
      payment_method: payment.payment_method,
      currency: payment.currency || 'USD',
      billing_cycle: billingCycle,
      due_date: dueDate
    }
  })

  // Calculate cost metrics
  const calculateCostMetrics = () => {
    const now = new Date()
    const currentMonth = format(now, 'yyyy-MM')
    const lastMonth = format(subMonths(now, 1), 'yyyy-MM')
    
    // Current month costs
    const currentMonthInvoices = invoices.filter(inv => {
      const invDate = inv.created_at || inv.date
      if (!invDate) return false
      try {
        const date = typeof invDate === 'string' ? new Date(invDate) : invDate
        return format(date, 'yyyy-MM') === currentMonth
      } catch {
        return false
      }
    })
    const currentMonthCost = currentMonthInvoices.reduce((sum, inv) => 
      sum + (parseFloat(inv.amount || inv.total || 0)), 0
    )

    // Last month costs
    const lastMonthInvoices = invoices.filter(inv => {
      const invDate = inv.created_at || inv.date
      if (!invDate) return false
      try {
        const date = typeof invDate === 'string' ? new Date(invDate) : invDate
        return format(date, 'yyyy-MM') === lastMonth
      } catch {
        return false
      }
    })
    const lastMonthCost = lastMonthInvoices.reduce((sum, inv) => 
      sum + (parseFloat(inv.amount || inv.total || 0)), 0
    )

    // Total costs
    const totalCost = invoices.reduce((sum, inv) => 
      sum + (parseFloat(inv.amount || inv.total || 0)), 0
    )

    // Forecast (based on current month trend)
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
    const currentDay = now.getDate()
    const forecast = currentDay > 0 ? (currentMonthCost / currentDay) * daysInMonth : currentMonthCost

    // Cost change percentage
    const costChange = lastMonthCost > 0 
      ? ((currentMonthCost - lastMonthCost) / lastMonthCost * 100).toFixed(1)
      : 0

    return {
      currentMonthCost,
      lastMonthCost,
      totalCost,
      forecast,
      costChange: parseFloat(costChange)
    }
  }

  // Generate monthly cost data for charts
  const generateMonthlyCostData = () => {
    const months = []
    const periodMonths = selectedPeriod === '6months' ? 6 : selectedPeriod === '12months' ? 12 : 3
    
    for (let i = periodMonths - 1; i >= 0; i--) {
      const monthDate = subMonths(new Date(), i)
      const monthKey = format(monthDate, 'yyyy-MM')
      const monthLabel = format(monthDate, 'MMM yyyy')
      
      const monthInvoices = invoices.filter(inv => {
        const invDate = inv.created_at || inv.date
        if (!invDate) return false
        try {
          const date = typeof invDate === 'string' ? new Date(invDate) : invDate
          return format(date, 'yyyy-MM') === monthKey
        } catch {
          return false
        }
      })
      
      const monthCost = monthInvoices.reduce((sum, inv) => 
        sum + (parseFloat(inv.amount || inv.total || 0)), 0
      )

      months.push({
        month: monthLabel,
        cost: monthCost,
        invoices: monthInvoices.length
      })
    }
    
    return months
  }

  // Generate cost breakdown by product
  const generateCostBreakdown = () => {
    const breakdown = {}
    
    // First, calculate from invoices (more accurate)
    invoices.forEach(inv => {
      const productName = inv.product_name || 'Unknown Product'
      const amount = parseFloat(inv.amount || inv.total || 0)
      
      if (!breakdown[productName]) {
        breakdown[productName] = {
          name: productName,
          cost: 0,
          count: 0,
          invoices: []
        }
      }
      
      breakdown[productName].cost += amount
      breakdown[productName].count += 1
      breakdown[productName].invoices.push({
        id: inv.id,
        invoice_number: inv.invoice_number || `INV-${inv.id}`,
        amount: amount,
        date: inv.created_at || inv.date
      })
    })
    
    // If no invoice data, use subscription data
    if (Object.keys(breakdown).length === 0) {
      subscriptions.forEach(sub => {
        const productName = sub.product_name || 'Unknown Product'
        const basePrice = parseFloat(sub.base_price || 0)
        const billingMultiplier = sub.billing_cycle === 'yearly' ? 1 : 12
        const monthlyCost = basePrice * billingMultiplier / 12
        
        if (!breakdown[productName]) {
          breakdown[productName] = {
            name: productName,
            cost: 0,
            count: 0,
            invoices: []
          }
        }
        
        breakdown[productName].cost += monthlyCost
        breakdown[productName].count += 1
      })
    }

    return Object.values(breakdown).sort((a, b) => b.cost - a.cost)
  }

  // Generate payment history
  const generatePaymentHistory = () => {
    return invoices
      .map(inv => ({
        id: inv.id,
        invoiceNumber: inv.invoice_number || `INV-${inv.id}`,
        date: inv.created_at || inv.date,
        amount: parseFloat(inv.amount || inv.total || 0),
        status: inv.status || 'pending',
        productName: inv.product_name || 'N/A',
        billingPeriod: inv.billing_period || (() => {
          try {
            const date = inv.created_at || inv.date
            if (!date) return 'N/A'
            const dateObj = typeof date === 'string' ? new Date(date) : date
            return format(dateObj, 'MMM yyyy')
          } catch {
            return 'N/A'
          }
        })(),
        due_date: inv.due_date
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
  }

  const metrics = calculateCostMetrics()
  const monthlyData = generateMonthlyCostData()
  const costBreakdown = generateCostBreakdown()
  const paymentHistory = generatePaymentHistory()

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  // PDF Generation Function - Professional Invoice Layout
  const generateInvoicePDF = (payment) => {
    try {
      if (typeof jsPDF === 'undefined') {
        throw new Error('jsPDF library is not loaded. Please ensure jspdf is installed.')
      }

      // Ensure organization data is available
      if (!organization) {
        console.warn('Organization data not available, attempting to fetch...')
        // Try to get organization from query cache or fetch it
        const cachedOrg = orgData?.data?.organization || orgData?.data?.data?.organization
        if (!cachedOrg) {
          alert('Organization information is not available. Please wait for data to load and try again.')
          return
        }
      }

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 20
      let yPos = margin

      // Get invoice data
      const invoiceNumber = payment.transaction_id || `SUB-${payment.id.substring(0, 8).toUpperCase()}`
      const invoiceDate = format(new Date(payment.payment_date || payment.created_at), 'MMM dd, yyyy')
      
      // Get organization data
      const currentOrg = organization || orgData?.data?.data?.organization
      const orgName = currentOrg?.name || 'N/A'
      const orgEmail = currentOrg?.email || 'N/A'
      
      if (!currentOrg || orgName === 'N/A' || orgEmail === 'N/A') {
        console.warn('Organization data not available:', { organization, orgData, currentOrg })
      }
      
      // Calculate due date (30 days from invoice date)
      const dueDate = new Date(payment.payment_date || payment.created_at)
      dueDate.setDate(dueDate.getDate() + 30)
      const formattedDueDate = format(dueDate, 'MMM dd, yyyy')
      
      const currency = payment.currency || 'USD'
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(payment.amount || 0)

      // Top Section - Company Name (Left) and INVOICE Title (Right)
      doc.setFontSize(24)
      doc.setFont('helvetica', 'bold')
      doc.text(orgName, margin, yPos)
      
      // INVOICE title on the right
      doc.setFontSize(28)
      doc.text('INVOICE', pageWidth - margin, yPos, { align: 'right' })
      yPos += 8

      // Email below company name
      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.text(orgEmail, margin, yPos)
      yPos += 10

      // Invoice # and Date on the left
      doc.setFontSize(11)
      doc.text(`Invoice #: ${invoiceNumber}`, margin, yPos)
      yPos += 6
      doc.text(`Date: ${invoiceDate}`, margin, yPos)
      yPos += 15

      // Table Header
      const tableStartY = yPos
      const col1X = margin
      const col2X = margin + 60
      const col3X = pageWidth - margin - 30
      
      // Table header background (light gray)
      doc.setFillColor(240, 240, 240)
      doc.rect(col1X, tableStartY - 6, pageWidth - (margin * 2), 8, 'F')
      
      // Table header text
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Product Name', col1X, tableStartY)
      doc.text('Billing Period', col2X, tableStartY)
      doc.text('Amount', col3X, tableStartY, { align: 'right' })
      yPos = tableStartY + 8

      // Table row
      doc.setFont('helvetica', 'normal')
      const productName = payment.product_name || 'N/A'
      const billingPeriod = payment.plan_name ? `${payment.plan_name} - ${(payment.billing_cycle || 'Monthly').charAt(0).toUpperCase() + (payment.billing_cycle || 'Monthly').slice(1)} Plan` : 'Monthly Plan'
      
      // Row background (white)
      doc.setFillColor(255, 255, 255)
      doc.rect(col1X, yPos - 6, pageWidth - (margin * 2), 8, 'F')
      
      // Row border
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.1)
      doc.line(col1X, yPos - 6, pageWidth - margin, yPos - 6)
      doc.line(col1X, yPos + 2, pageWidth - margin, yPos + 2)
      
      doc.text(productName, col1X, yPos)
      doc.text(billingPeriod, col2X, yPos)
      doc.text(formattedAmount, col3X, yPos, { align: 'right' })
      yPos += 15

      // Status, Created Date, and Due Date
      doc.setFontSize(10)
      const status = payment.payment_status === 'completed' ? 'Paid' : 
                     payment.payment_status === 'pending' ? 'Pending' : 
                     payment.payment_status === 'failed' ? 'Failed' : 'Pending'
      
      doc.text(`Status: ${status}`, margin, yPos)
      yPos += 6
      doc.text(`Created Date: ${invoiceDate}`, margin, yPos)
      yPos += 6
      doc.text(`Due Date: ${formattedDueDate}`, margin, yPos)

      // Save PDF
      const fileName = `Invoice-${invoiceNumber}.pdf`
      doc.save(fileName)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please check the browser console for details.')
    }
  }

  const handleViewInvoice = (paymentId) => {
    setSelectedInvoiceId(paymentId)
    setShowInvoiceModal(true)
  }

  const handleDownloadInvoice = (payment) => {
    // Find the full payment object from rawPayments
    const fullPayment = rawPayments.find(p => p.id === payment.id)
    if (fullPayment) {
      generateInvoicePDF(fullPayment)
    } else {
      generateInvoicePDF(payment)
    }
  }

  if (paymentsLoading || subscriptionsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
      <div>
          <h3 className="text-2xl font-bold text-gray-900">Billing & Cost Management</h3>
          <p className="text-sm text-gray-600 mt-1">
            Monitor costs, payments, and billing information
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="12months">Last 12 Months</option>
          </select>
        </div>
      </div>

      {/* Cost Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Current Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(metrics.currentMonthCost)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.costChange >= 0 ? '+' : ''}{metrics.costChange}% from last month
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Forecasted</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(metrics.forecast)}
                </p>
                <p className="text-xs text-gray-500 mt-1">This month</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Paid</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(metrics.totalCost)}
                </p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {invoices.filter(inv => inv.status === 'paid').length} paid
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Cost Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Monthly Cost Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => `$${value.toFixed(0)}`} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                labelStyle={{ color: '#374151' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="cost" 
                stroke="#3B82F6" 
                strokeWidth={2}
                name="Cost"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cost Breakdown and Payment History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Breakdown by Product */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Cost Breakdown by Product
            </CardTitle>
          </CardHeader>
          <CardContent>
            {costBreakdown.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={costBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(Number.isFinite(percent) ? percent * 100 : 0).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="cost"
                    >
                      {costBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-3">
                  {costBreakdown.map((item, index) => (
                    <div key={index} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="font-medium text-gray-900">{item.name}</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(item.cost)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{item.count} {item.count === 1 ? 'invoice' : 'invoices'}</span>
                        <span>{(metrics.totalCost > 0 ? (item.cost / metrics.totalCost) * 100 : 0).toFixed(1)}% of total</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No subscription data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Payments Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Monthly Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `$${value.toFixed(0)}`} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: '#374151' }}
                />
                <Legend />
                <Bar dataKey="cost" fill="#3B82F6" name="Monthly Cost" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Payment History Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Payment History
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleSection('payments')}
            >
              {expandedSection === 'payments' ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSection === 'payments' && (
          <CardContent className="p-0">
            {paymentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice #
                    </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Billing Period
                      </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {paymentHistory.map((payment) => {
                      return (
                      <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                            {payment.invoiceNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-medium">
                            {payment.productName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {payment.billingPeriod}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                            {(() => {
                              try {
                                const date = typeof payment.date === 'string' ? new Date(payment.date) : payment.date
                                return format(date, 'MMM dd, yyyy')
                              } catch {
                                return 'Invalid date'
                              }
                            })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                            {payment.due_date ? (
                              format(payment.due_date, 'MMM dd, yyyy')
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(payment.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            payment.status === 'paid' 
                            ? 'bg-green-100 text-green-800'
                              : payment.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                            {payment.status === 'paid' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {payment.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                            {payment.status === 'failed' && <AlertCircle className="h-3 w-3 mr-1" />}
                            {payment.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewInvoice(payment.id)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="View Invoice"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const fullPayment = rawPayments.find(p => p.id === payment.id)
                              if (fullPayment) {
                                handleDownloadInvoice(fullPayment)
                              }
                            }}
                            className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                            title="Download Invoice"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    )})}
                </tbody>
              </table>
              </div>
            ) : (
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment History</h3>
                <p className="text-gray-600">
                  Payment history will appear here once invoices are generated.
                </p>
              </CardContent>
            )}
          </CardContent>
        )}
      </Card>

      {/* Billing Periods Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Billing Periods Summary
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleSection('periods')}
            >
              {expandedSection === 'periods' ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSection === 'periods' && (
          <CardContent>
            <div className="space-y-4">
              {monthlyData.map((period, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{period.month}</p>
                      <p className="text-sm text-gray-500">
                        {period.invoices} invoice{period.invoices !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(period.cost)}
                    </p>
                    <p className="text-xs text-gray-500">Total cost</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
        </Card>

      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Payment Methods
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleSection('paymentMethods')}
            >
              {expandedSection === 'paymentMethods' ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        {expandedSection === 'paymentMethods' && (
          <CardContent>
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Methods</h3>
              <p className="text-gray-600">
                Payment methods will appear here once they are added.
              </p>
            </div>
          </CardContent>
        )}
        </Card>

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false)
          setSelectedInvoiceId(null)
        }}
        paymentId={selectedInvoiceId}
        organizationId={organizationId}
        onDownload={handleDownloadInvoice}
      />
    </div>
  )
}

